/**
 * "Has this message already alerted?" bookkeeping for new-message toasts.
 *
 * A child-channel message is emitted TWICE over the socket — once on the child
 * channel and once as its All-Chats mirror — so a user who sits on both the
 * child and the lead would otherwise get two toasts for one message. The mirror
 * carries `mirrorOfId = <child message id>`, so keying on `mirrorOfId ?? id`
 * collapses the pair onto one logical message. Lead users who are NOT on the
 * child only ever receive the mirror, and still get their one alert.
 *
 * Entries expire so the map can't grow without bound in a long-lived tab.
 */

export const ALERT_SEEN_MS = 10_000;

export interface AlertableMessage {
  id?: string | number | null;
  /** Set on an All-Chats mirror; points at the child-channel original. */
  mirrorOfId?: string | number | null;
}

const seen = new Map<string, number>();

/** The logical-message key both copies of a mirrored message share. */
export function chatAlertKey(msg: AlertableMessage | null | undefined): string {
  return String(msg?.mirrorOfId ?? msg?.id ?? "");
}

/**
 * Claim the right to alert for `msg`. Returns false when the other copy of the
 * same logical message already claimed it inside the window.
 *
 * A message with no usable id always claims — better a rare double toast than a
 * silently swallowed alert.
 */
export function claimChatAlert(
  msg: AlertableMessage | null | undefined,
  now: number = Date.now(),
): boolean {
  const key = chatAlertKey(msg);
  if (!key) return true;

  for (const [k, at] of seen) {
    if (now - at > ALERT_SEEN_MS) seen.delete(k);
  }
  if (seen.has(key)) return false;
  seen.set(key, now);
  return true;
}

/** Test seam — drops all claims. */
export function resetChatAlertDedupe() {
  seen.clear();
}
